import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, MenuItem, TextField } from "@mui/material";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import PrintIcon from "assets/printer.svg";
import TabTitle from "components/Elements/TabTitle";
import DetailsPanel from "components/Elements/DetailsPanel";
import DeleteDialog from "components/Elements/DeleteDialog";
import CommonApplicationTable from "components/Modules/CommonApplicationTable";
import SearchField from "components/Elements/SearchField/SearchField";
import MobileSearchField from "components/Elements/SearchField/MobileSearchField";
import { useSearch } from "hooks/useSearch";
import { localeOptions } from "helpers/localeOptions";
import { getLocalStorageData } from "helpers/utils";
import { FULL_ACCESS } from "constants/AccessTypes/AccessTypes";
import {
	createGlossaryEntry,
	exportGlossaryCsv,
	getGlossaryEntries,
	updateGlossaryEntry,
} from "services/ai";
import { showError, showSuccess } from "../../../../../redux/common/actions";
import AddEntryDialog from "./AddEntryDialog";
import BulkImportDialog from "./BulkImportDialog";

const ALL_LANGUAGES = "__all__";

const localeLabel = (value) =>
	localeOptions.find((o) => o.value === value)?.label ?? value;

// CommonApplicationTable's row sort + useSearch both pivot on `.name`. Project
// each glossary row into a flat shape with a synthetic `name` that combines the
// fields we want to be searchable so the shared components work without
// modification.
const toRow = (entry) => ({
	id: entry.id,
	siteAppID: entry.siteAppID,
	sourceTerm: entry.sourceTerm,
	targetTerm: entry.targetTerm,
	targetLanguage: entry.targetLanguage,
	languageLabel: localeLabel(entry.targetLanguage),
	context: entry.context ?? "",
	name: [
		entry.sourceTerm,
		entry.targetTerm,
		entry.context,
		localeLabel(entry.targetLanguage),
	]
		.filter(Boolean)
		.join(" "),
});

const TranslationGlossary = ({ state, dispatch }) => {
	const { appId } = useParams();
	const reduxDispatch = useDispatch();
	const siteAppId = Number(appId);
	const appName = state?.details?.data?.application?.name ?? "";

	// Same gate the nav-header Add+ icon uses (CommonHeaderWrapper). Users
	// without Settings Full access shouldn't see write affordances; the Edit
	// and Remove row-menu entries are likewise hidden below.
	const { position } = getLocalStorageData("me") || {};
	const hasFullSettingsAccess = position?.settingsAccess === FULL_ACCESS;

	const [loading, setLoading] = useState(false);
	const [showBulk, setShowBulk] = useState(false);
	const [editEntry, setEditEntry] = useState(null);
	const [deleteId, setDeleteId] = useState(null);
	const [filterLanguage, setFilterLanguage] = useState(ALL_LANGUAGES);
	const [lastUsedLocale, setLastUsedLocale] = useState(null);
	const [isDownloading, setIsDownloading] = useState(false);

	const {
		allData,
		setAllData,
		handleSearch,
		searchedData,
		searchQuery,
		setSearchData,
		setSearchQuery,
	} = useSearch();

	const fetchEntries = useCallback(async () => {
		if (!siteAppId) return;
		setLoading(true);
		const response = await getGlossaryEntries(siteAppId);
		if (response?.status) {
			const nextRows = (response.data ?? []).map(toRow);
			setAllData(nextRows);
			if (searchQuery.length > 0) {
				const regex = new RegExp(searchQuery, "gi");
				setSearchData(nextRows.filter((row) => row.name.match(regex)));
			} else {
				setSearchData(nextRows);
			}
		} else {
			reduxDispatch(showError("Failed to load glossary entries"));
		}
		setLoading(false);
	}, [siteAppId, reduxDispatch, searchQuery, setAllData, setSearchData]);

	useEffect(() => {
		fetchEntries();
	}, [fetchEntries]);

	const openBulkImport = useCallback(() => {
		setShowBulk(true);
	}, []);

	const handleDownloadCsv = useCallback(async () => {
		setIsDownloading(true);
		const lang =
			filterLanguage === ALL_LANGUAGES ? null : filterLanguage;
		const res = await exportGlossaryCsv(siteAppId, lang);
		if (!res.status || !res.data) {
			setIsDownloading(false);
			reduxDispatch(showError("Failed to download glossary CSV"));
			return;
		}
		const url = window.URL.createObjectURL(res.data);
		const a = document.createElement("a");
		a.href = url;
		a.download = lang
			? `glossary-siteapp-${siteAppId}-${lang}.csv`
			: `glossary-siteapp-${siteAppId}.csv`;
		document.body.appendChild(a);
		a.click();
		a.remove();
		window.URL.revokeObjectURL(url);
		setIsDownloading(false);
	}, [filterLanguage, reduxDispatch, siteAppId]);

	useEffect(() => {
		if (!hasFullSettingsAccess) {
			dispatch({ type: "CLEAR_HEADER_IMPORT" });
			dispatch({ type: "CLEAR_HEADER_ICON_ACTION" });
			return undefined;
		}

		dispatch({
			type: "SET_HEADER_IMPORT",
			payload: {
				visible: true,
				label: "Import from CSV",
				onClick: openBulkImport,
			},
		});
		if (allData.length > 0) {
			dispatch({
				type: "SET_HEADER_ICON_ACTION",
				payload: {
					iconSrc: PrintIcon,
					title: "Download CSV",
					isLoading: isDownloading,
					loadingNode: <CircularProgress size={24} />,
					onClick: handleDownloadCsv,
				},
			});
		} else {
			dispatch({ type: "CLEAR_HEADER_ICON_ACTION" });
		}

		return () => {
			dispatch({ type: "CLEAR_HEADER_IMPORT" });
			dispatch({ type: "CLEAR_HEADER_ICON_ACTION" });
		};
	}, [
		allData.length,
		dispatch,
		handleDownloadCsv,
		hasFullSettingsAccess,
		isDownloading,
		openBulkImport,
	]);

	const counts = useMemo(() => {
		return allData.reduce((acc, e) => {
			acc[e.targetLanguage] = (acc[e.targetLanguage] ?? 0) + 1;
			return acc;
		}, {});
	}, [allData]);

	const tableData = useMemo(() => {
		const baseList = searchQuery.length === 0 ? allData : searchedData;
		if (filterLanguage === ALL_LANGUAGES) return baseList;
		return baseList.filter((e) => e.targetLanguage === filterLanguage);
	}, [allData, searchedData, searchQuery, filterLanguage]);

	const handleAddSingle = async (entry) => {
		if (editEntry) {
			const response = await updateGlossaryEntry(editEntry.id, {
				siteAppID: editEntry.siteAppID,
				...entry,
			});
			if (!response?.status) {
				reduxDispatch(showError("Failed to update glossary entry"));
				return;
			}
			reduxDispatch(showSuccess("Glossary entry updated"));
			setEditEntry(null);
			await fetchEntries();
			return;
		}

		const response = await createGlossaryEntry({
			siteAppID: siteAppId,
			...entry,
		});
		if (!response?.status) {
			reduxDispatch(showError("Failed to add glossary entry"));
			return;
		}
		setLastUsedLocale(entry.targetLanguage);
		dispatch({ type: "ADD_TOGGLE" });
		await fetchEntries();
	};

	const handleEdit = (id) => {
		const row = allData.find((r) => r.id === id);
		if (row) setEditEntry(row);
	};

	const closeAddOrEdit = () => {
		if (editEntry) setEditEntry(null);
		if (state?.showAdd) dispatch({ type: "ADD_TOGGLE" });
	};

	// The dialog uploads the CSV itself via importGlossaryCsv and just hands
	// the response back. We only react: toast, close, refresh.
	const handleBulkImport = async ({ response }) => {
		if (!response?.status) {
			reduxDispatch(
				showError(response?.data?.detail ?? "CSV import failed")
			);
			return;
		}
		const { imported = 0, replaced = 0 } = response.data ?? {};
		reduxDispatch(
			showSuccess(
				`Imported ${imported} entries${
					replaced > 0 ? ` (${replaced} replaced existing)` : ""
				}`
			)
		);
		setShowBulk(false);
		setSearchQuery("");
		setFilterLanguage(ALL_LANGUAGES);
		await fetchEntries();
	};

	const handleRemoveData = (id) => {
		setAllData(allData.filter((x) => x.id !== id));
		setSearchData(searchedData.filter((x) => x.id !== id));
	};

	return (
		<div>
			<TabTitle title={`${appName} Translation Glossary`} />

			<AddEntryDialog
				open={!!state?.showAdd || editEntry !== null}
				closeHandler={closeAddOrEdit}
				onSubmit={handleAddSingle}
				defaultLocale={
					lastUsedLocale ??
					(filterLanguage === ALL_LANGUAGES ? null : filterLanguage)
				}
				editEntry={editEntry}
			/>
			<BulkImportDialog
				open={showBulk}
				closeHandler={() => setShowBulk(false)}
				onSubmit={handleBulkImport}
				existingEntries={allData}
				defaultLocale={
					filterLanguage === ALL_LANGUAGES ? null : filterLanguage
				}
				siteAppId={siteAppId}
			/>
			<DeleteDialog
				entityName="glossary entry"
				open={deleteId !== null}
				closeHandler={() => setDeleteId(null)}
				deleteID={deleteId}
				deleteEndpoint="/api/AIGlossaries"
				handleRemoveData={handleRemoveData}
			/>

			<div className="detailsContainer">
				<DetailsPanel
					header="Translation Glossary"
					dataCount={allData.length}
					description="Site App–specific terminology used by AI translation. Add entries to control how source terms are rendered in the target language."
				/>
				<SearchField
					searchQuery={searchQuery}
					setSearchQuery={handleSearch}
					header="glossary entries"
				/>
				<MobileSearchField
					searchQuery={searchQuery}
					setSearchQuery={handleSearch}
					header="glossary entries"
				/>
			</div>

			<Box
				sx={{
					display: "flex",
					gap: 2,
					alignItems: "center",
					flexWrap: "wrap",
					mb: 2,
				}}
			>
				<TextField
					select
					label="Locale"
					size="small"
					value={filterLanguage}
					onChange={(e) => setFilterLanguage(e.target.value)}
					sx={{ minWidth: 240 }}
				>
					<MenuItem value={ALL_LANGUAGES}>
						All ({allData.length})
					</MenuItem>
					{Object.keys(counts)
						.sort((a, b) =>
							localeLabel(a).localeCompare(localeLabel(b))
						)
						.map((code) => (
							<MenuItem key={code} value={code}>
								{localeLabel(code)} ({counts[code]})
							</MenuItem>
						))}
				</TextField>
				<Box sx={{ flexGrow: 1 }} />
			</Box>

			<CommonApplicationTable
				isReadOnly={state?.isReadOnly}
				setData={setAllData}
				setSearch={setSearchData}
				searchedData={tableData}
				searchQuery={searchQuery}
				data={tableData}
				columns={["sourceTerm", "targetTerm", "languageLabel", "context"]}
				headers={["Source term", "Target term", "Language", "Context"]}
				isLoading={loading}
				menuData={[
					{
						name: "Edit",
						handler: handleEdit,
						isDelete: false,
					},
					{
						name: "Remove",
						handler: (id) => setDeleteId(id),
						isDelete: true,
					},
				]}
			/>
		</div>
	);
};

export default TranslationGlossary;
